<template>
  <div id="app">
    <div class="app-container">
      <!-- 账户汇总 -->
      <div class="account-summary">
        <div class="assets-card">
          <div class="assets-row">
            <div class="label clickable" @click="toggleAssetMask">
              账户资产 {{ maskAssets ? '🙈' : '👁️' }}
            </div>
            <div class="date-info clickable" @click="toggleProfitDisplay">
              {{ showProfitRate ? '⇄ 当日收益率' : '⇄ 当日总收益' }}
            </div>
          </div>
          <div class="amount-row">
            <div class="total-amount">{{ displayTotalAssets }}</div>
            <div class="total-profit" :class="getProfitClass(dailyProfit)">
              <span v-if="showProfitRate">
                {{ displayDailyProfitRate }}
              </span>
              <span v-else>
                {{ displayDailyProfit }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- 列表控制栏 -->
      <div class="list-controls">
        <div class="market-status" v-if="isMarketClosed">休市中</div>
        <div class="market-status" v-else>交易中</div>
        <div class="header-stats">
          <div class="header-col">当日涨幅</div>
          <div class="header-col">当日收益</div>
        </div>
      </div>

      <!-- 基金列表 -->
      <div class="fund-list">
        <div class="fund-item" v-for="(fund, index) in funds" :key="index" @click="openFundDetail(fund)">
          <div class="fund-main">
            <div class="fund-name">{{ fund.code }} - {{ fund.name }}</div>
            <div class="fund-holdings">{{ maskAssets ? '******' : '¥ ' + formatMoney(fund.holding) }}</div>
          </div>
          
          <div class="fund-stats">
            <div class="stat-col">
              <div class="stat-value" :class="getProfitClass(fund.dailyRate)">
                {{ formatRate(fund.dailyRate) }}%
              </div>
              <!-- <div class="stat-sub">{{ fund.netValue }} · {{ formatGzTime(fund.gztime) }}</div> -->
            </div>
            <div class="stat-col">
              <div class="stat-value" :class="getProfitClass(fund.dailyProfit)">
                {{ fund.dailyProfit > 0 ? '+' : '' }}{{ fund.dailyProfit }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 底部操作区 -->
      <div class="list-footer">
        <div class="left-actions">
          <div class="action-btn icon-btn" @click="showSyncModal = true">
            <span class="icon">🔄</span> 同步
          </div>
          <div class="action-btn icon-btn" @click="triggerRefresh(true)">
            <span class="icon">⚡</span> 刷新
          </div>
        </div>
        
        <div class="data-source-toggle">
          <div
            class="toggle-item"
            :class="{ active: dataSource === 'api' }"
            @click="setDataSource('api')"
          >
            数据源1
          </div>
          <div
            class="toggle-item"
            :class="{ active: dataSource === 'holdings' }"
            @click="setDataSource('holdings')"
          >
            数据源2
          </div>
        </div>
      </div>
    </div>
    
    <SyncModal 
      :visible="showSyncModal" 
      @close="showSyncModal = false" 
      @confirm="handleSyncConfirm"
    />

    <FundDetail
      :visible="showDetailModal"
      :fund="selectedFund"
      @close="closeFundDetail"
      @action="openFundAction(selectedFund)"
    />

    <FundActionModal
      :visible="showActionModal"
      :fund="selectedFund"
      @close="closeFundAction"
      @update="handleFundUpdate"
      @delete="handleFundDelete"
    />
  </div>
</template>

<script>
import { db } from './utils/db'
import { formatMoney, formatRate, getProfitClass, formatGzTime } from './utils/format'
import SyncModal from './components/SyncModal.vue'
import FundActionModal from './components/FundActionModal.vue'
import FundDetail from './components/FundDetail.vue'

export default {
  name: 'App',
  components: {
    SyncModal,
    FundActionModal,
    FundDetail
  },
  data() {
    return {
      funds: [],
      showSyncModal: false,
      refreshTimer: null,
      showActionModal: false,
      showDetailModal: false,
      selectedFund: null,
      lastUpdateTime: '',
      dataSource: 'api',
      refreshCountdown: 10,
      refreshCountdownTimer: null,
      isRefreshing: false,
      maskAssets: false,
      showProfitRate: false,
      isMarketClosed: false,
      holdingsErrorStates: (() => {
        try {
          return JSON.parse(localStorage.getItem('holdingsErrorStates') || '{}');
        } catch (e) {
          return {};
        }
      })()
    }
  },
  computed: {
    totalAssets() {
      return this.funds.reduce((sum, fund) => sum + fund.holding, 0);
    },
    dailyProfit() {
      return this.funds.reduce((sum, fund) => sum + fund.dailyProfit, 0);
    },
    dailyProfitRate() {
      if (!this.totalAssets) return 0;
      return (this.dailyProfit / this.totalAssets) * 100;
    },
    displayTotalAssets() {
      return this.maskAssets ? '******' : this.formatMoney(this.totalAssets);
    },
    displayDailyProfit() {
      if (this.maskAssets) return '******';
      return `${this.dailyProfit > 0 ? '+' : ''}${this.formatMoney(this.dailyProfit)}`;
    },
    displayDailyProfitRate() {
      if (this.maskAssets) return '******';
      const rate = this.dailyProfitRate;
      const sign = rate > 0 ? '+' : '';
      return `${sign}${rate.toFixed(2)}%`;
    }
  },
  async created() {
    const initialData = [
    ];

    try {
      // 先获取现有数据
      let currentFunds = await db.getAllFunds();
      
      // 自动检测并清理旧数据（没有 code 的数据）
      const hasInvalidData = currentFunds.some(f => !f.code);
      if (hasInvalidData) {
        // console.log('Detected legacy data without codes. Clearing DB to apply new structure...');
        await db.clearFunds();
        currentFunds = []; // 重置为空，触发下方的 initMockData
      }

      const savedSource = localStorage.getItem('dataSource');
      this.dataSource = savedSource || 'api';
      await db.initMockData(initialData);
      this.funds = await db.getAllFunds();
      // console.log('Funds loaded, calling startAutoRefresh...');
      
      // 初始检查休市状态
      this.checkMarketStatus();
      
      // 应用启动时，强制执行一次刷新（忽略休市状态），以确保数据是最新的
      // console.log('App started, forcing initial refresh...');
      await this.refreshAllFundsNow();

      // 启动自动刷新
      this.startAutoRefresh();
      this.startRefreshCountdown();
    } catch (error) {
      console.error('Failed to load data from IndexedDB:', error);
      // Fallback to initial data if DB fails
      this.funds = initialData;
    }
  },
  beforeDestroy() {
    this.stopAutoRefresh();
    this.stopRefreshCountdown();
  },
  methods: {
    formatMoney,
    formatRate,
    getProfitClass,
    formatGzTime,
    startAutoRefresh() {
      // console.log('startAutoRefresh called');
      
      // 启动调度器，每1秒检查是否有需要更新的基金
      this.refreshTimer = setInterval(() => {
        this.scheduler();
      }, 1000);
    },
    toggleAssetMask() {
      this.maskAssets = !this.maskAssets;
    },
    toggleProfitDisplay() {
      this.showProfitRate = !this.showProfitRate;
    },
    stopAutoRefresh() {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer);
        this.refreshTimer = null;
      }
    },
    startRefreshCountdown() {
      if (this.refreshCountdownTimer) return;
      this.refreshCountdown = 10;
      this.refreshCountdownTimer = setInterval(async () => {
        if (this.isRefreshing) return;
        
        // 休市时不倒计时
        if (this.isMarketClosed) return;

        this.refreshCountdown -= 1;
        if (this.refreshCountdown <= 0) {
          await this.triggerRefresh(false);
          this.refreshCountdown = 10;
        }
      }, 1000);
    },
    stopRefreshCountdown() {
      if (this.refreshCountdownTimer) {
        clearInterval(this.refreshCountdownTimer);
        this.refreshCountdownTimer = null;
      }
    },
    // 调度器：负责检查并触发更新
    scheduler() {
      if (this.isRefreshing) return;
      
      // 休市判断
      this.checkMarketStatus();
      if (this.isMarketClosed) return;

      const now = Date.now();
      
      // 1. 初始化：如果 funds 还没有 nextUpdateTime，给它们分配一个
      // 错峰初始化：防止启动时瞬间并发
      let uninitializedCount = 0;
      this.funds.forEach((fund, index) => {
        if (!fund.nextUpdateTime) {
          // 初始错峰：每个基金间隔 2 秒
          // 修改逻辑：如果是 App 刚启动（created），为了尽快看到数据，前几个基金应该稍微快一点
          // 但为了防封，依然保持间隔。
          // 优化：将初始间隔缩短到 1.5 秒
          fund.nextUpdateTime = now + (index * 1500);
          uninitializedCount++;
        }
      });
      if (uninitializedCount > 0) return; // 等待下一轮 tick

      // 2. 查找需要更新的基金
      // 为了防封，每次 tick 最多只更新 1 个
      let candidate = null;
      
      for (const fund of this.funds) {
        if (fund.code && now >= fund.nextUpdateTime) {
          // 找到一个到期的
          candidate = fund;
          break; // 立即跳出，保证只取一个
        }
      }

      if (candidate) {
        // 立即更新下次时间，防止并发 tick 重复选中
        // 先临时推迟 30 秒，等 updateSingleFund 完成后再设置准确的下一次时间
        candidate.nextUpdateTime = Date.now() + 30000;
        this.updateSingleFund(candidate);
      }
    },
    async updateSingleFund(fund) {
      try {
        // console.log(`[Scheduler] Updating ${fund.code}...`);
        if (this.dataSource === 'holdings') {
          const estimateRate = await this.fetchHoldingsEstimate(fund.code);
          const netValue = parseFloat(fund.netValue) || 1;
          if (!fund.share) {
            fund.share = fund.holding / netValue;
          }
          const estNet = netValue * (1 + estimateRate / 100);
          const dailyProfit = fund.share * (estNet - netValue);
          fund.dailyRate = estimateRate;
          fund.netValue = netValue;
          fund.dailyProfit = parseFloat(dailyProfit.toFixed(2));
          fund.gztime = new Date().toLocaleTimeString('zh-CN', { hour12: false });
        } else {
          const fundData = await this.fetchFundData(fund.code);
          
          // 更新数据逻辑...
          const netValue = parseFloat(fundData.dwjz);
          const estNet = parseFloat(fundData.gsz);
          const dailyRate = parseFloat(fundData.gszzl);

          if (!fund.share) {
            fund.share = fund.holding / netValue;
          }

          fund.holding = fund.share * netValue;
          const dailyProfit = fund.share * (estNet - netValue);

          fund.name = fundData.name;
          fund.dailyRate = dailyRate;
          fund.netValue = netValue;
          fund.dailyProfit = parseFloat(dailyProfit.toFixed(2));
          fund.gztime = fundData.gztime;
        }
        
        // 更新成功，设定下一次更新时间
        // 基础间隔 5s + 随机 0-5s = 5~10s 不等
        // 这样实现了“频次不一样”且完全分散
        const delay = 5000 + Math.random() * 5000;
        fund.nextUpdateTime = Date.now() + delay;
        // console.log(`[Scheduler] Updated ${fund.code}, next update in ${(delay/1000).toFixed(1)}s`);

        // 持久化更新到 IndexedDB
        // 注意：我们在这里保存，是为了确保页面刷新后数据依然是最新的
        // 只需要保存关键字段即可，避免保存不必要的运行时状态（如 nextUpdateTime）
        // 但 db.updateFund 可能是全量替换，所以最好是 clone 一份
        // 这里的 fund 对象是 this.funds 里的引用，直接传进去即可
        // 需要去掉 nextUpdateTime 吗？IndexedDB 里存这个也没坏处，甚至可以用来恢复状态
        await db.updateFund(JSON.parse(JSON.stringify(fund)));

      } catch (error) {
        if (error.message === 'BLOCKED') {
          // 如果被熔断，不再尝试，或者很久以后再试
          // console.warn(`[Scheduler] ${fund.code} is blocked, skipping update.`);
          fund.nextUpdateTime = Date.now() + 3600000; // 1 hour
          return;
        }
        // console.warn(`[Scheduler] Failed to update ${fund.code}:`, error);
        // 失败后稍后重试（5秒后）
        fund.nextUpdateTime = Date.now() + 5000;
      }
    },
    async refreshAllFundsNow() {
      if (this.isRefreshing) return;
      this.isRefreshing = true;
      for (const fund of this.funds) {
        if (fund.code) {
          await this.updateSingleFund(fund);
        }
      }
      this.isRefreshing = false;
    },
    async triggerRefresh(manual) {
      if (!manual && this.isMarketClosed) {
        // console.log('Market closed, skip auto refresh');
        return;
      }
      if (manual) {
        this.refreshCountdown = 10;
      }
      await this.refreshAllFundsNow();
    },
    // 旧方法保留但不再直接使用，或者重构掉
    async refreshAllFunds() {
       // Deprecated by scheduler
       console.warn('refreshAllFunds is deprecated');
    },
    // 打开基金详情弹窗
    openFundDetail(fund) {
      if (!fund) return;
      this.selectedFund = { ...fund };
      this.showDetailModal = true;
    },
    closeFundDetail() {
      this.showDetailModal = false;
      this.selectedFund = null;
    },
    // 打开基金操作弹窗
    openFundAction(fund) {
      // console.log('openFundAction triggered', fund); // 添加调试日志
      if (!fund) {
        console.error('Fund object is missing/null');
        return;
      }
      // console.log('Opening fund action for:', fund);
      this.selectedFund = { ...fund }; // 浅拷贝，避免直接修改列表数据
      this.showActionModal = true;
      // console.log('Modal visible set to true');
    },
    closeFundAction() {
      this.showActionModal = false;
      this.selectedFund = null;
    },
    // 处理基金更新（加减仓）
    async handleFundUpdate(updatedFund) {
      try {
        // 如果只是加减金额，我们需要更新份额
        // 假设操作是基于当前最新净值进行的
        // 重新计算份额：份额 = 新的总金额 / 当前净值
        // 注意：这种方式简化了逻辑，假设用户是按当前净值调整的
        if (updatedFund.netValue > 0) {
          updatedFund.share = updatedFund.holding / updatedFund.netValue;
        }

        await db.updateFund(updatedFund);
        // 更新成功后刷新列表
        this.funds = await db.getAllFunds();
        // 立即触发一次行情刷新（因为持仓变了，收益也变了）
        this.refreshAllFundsNow();
      } catch (error) {
        console.error('Failed to update fund:', error);
        alert('更新失败，请重试');
      }
    },
    // 处理基金删除
    async handleFundDelete(fundCode) {
      if (!fundCode) {
        console.error('Missing fund code for deletion');
        return;
      }
      try {
        await db.deleteFund(fundCode);
        this.funds = await db.getAllFunds();
      } catch (error) {
        console.error('Failed to delete fund:', error);
        alert('删除失败，请重试');
      }
    },
    // 行情获取：优先走 /api/fund（HTTPS），失败则回退 JSONP（本地开发）
    fetchFundData(code) {
      const url = `/api/fund?code=${code}`;
      // console.log(`[Fetch] Requesting ${url}`);
      return fetch(url, { cache: 'no-store' })
        .then(r => {
          if (!r.ok) throw new Error('Fund fetch failed');
          return r.json();
        })
        .then(data => {
          // console.log(`[Fetch] Success for ${code}:`, data);
          return data;
        })
        .catch((err) => {
          // console.warn(`[Fetch] Failed for ${code}, falling back to JSONP:`, err);
          // 在 HTTPS 环境下禁用 HTTP JSONP 降级，避免 Mixed Content 错误
          if (location.protocol === 'https:') {
            console.error('Cannot fallback to JSONP in HTTPS environment');
            throw err;
          }
          return this.fetchFundDataJsonp(code);
        });
    },
    // JSONP 回退
    fetchFundDataJsonp(code) {
      // console.log(`[JSONP] Requesting for ${code}`);
      return new Promise((resolve, reject) => {
        const timestamp = new Date().getTime();
        const callbackName = `jsonpgz_${code}_${timestamp}`;
        const url = `http://fundgz.1234567.com.cn/js/${code}.js?rt=${timestamp}&callback=${callbackName}`;
        
        // Define unique callback
        window[callbackName] = (data) => { 
          // console.log(`[JSONP] Success for ${code}:`, data);
          resolve(data); 
          cleanup(); 
        };
        
        // Fallback for default 'jsonpgz' if API doesn't support custom callback param
        // Some older APIs strictly use 'jsonpgz'
        if (!window.jsonpgz) {
             window.jsonpgz = () => {
                 // Try to guess which request this is for, or just resolve if we only have one flying
                 // This is imperfect but a safety net
                 // console.log(`[JSONP] Global callback triggered`);
             }
        }

        const script = document.createElement('script');
        script.src = url;
        script.onerror = () => { 
          console.error(`[JSONP] Error loading script for ${code}`);
          reject(new Error(`Failed to load fund data for ${code}`)); 
          cleanup(); 
        };
        
        document.body.appendChild(script);
        function cleanup() {
          if (script.parentNode) script.parentNode.removeChild(script);
          delete window[callbackName];
        }
      });
    },
    async fetchHoldingsEstimate(code) {
      // 熔断检查
      const errorState = this.holdingsErrorStates[code] || { count: 0, blocked: false };
      
      // 辅助方法：降级到 api/fund 获取涨幅
      const fallbackToFundApi = async () => {
        console.warn(`[Fallback] Using api/fund for ${code} due to holdings API error/block.`);
        const data = await this.fetchFundData(code);
        return parseFloat(data.gszzl);
      };

      if (errorState.blocked) {
        return fallbackToFundApi();
      }

      try {
        const res = await fetch(`/api/holdings?code=${code}`, { cache: 'no-store' });
        if (!res.ok) throw new Error('Holdings fetch failed');
        const data = await res.json();
        
        // 检查空数据
        if (!data.stocks || data.stocks.length === 0) {
           throw new Error('EMPTY_STOCKS');
        }

        // 成功，重置错误计数
        if (errorState.count > 0) {
          this.$set(this.holdingsErrorStates, code, { count: 0, blocked: false });
          localStorage.setItem('holdingsErrorStates', JSON.stringify(this.holdingsErrorStates));
        }

        const stocks = data.stocks || [];
        let total = 0;
        stocks.forEach(stock => {
          const percent = parseFloat(stock.percent);
          const zdf = parseFloat(stock.zdf);
          if (!isNaN(percent) && !isNaN(zdf)) {
            total += (percent / 100) * zdf;
          }
        });
        return parseFloat(total.toFixed(2));
      } catch (error) {
        // 记录错误并判断是否熔断
        if (!this.holdingsErrorStates[code]) {
          this.$set(this.holdingsErrorStates, code, { count: 0, blocked: false });
        }
        
        const state = this.holdingsErrorStates[code];
        state.count += 1;
        
        if (state.count >= 3) {
          state.blocked = true;
          console.error(`[Circuit Breaker] ${code} blocked due to 3 consecutive errors (or empty data).`);
        }
        
        localStorage.setItem('holdingsErrorStates', JSON.stringify(this.holdingsErrorStates));
        
        // 触发降级逻辑
        return fallbackToFundApi();
      }
    },
    setDataSource(source) {
      if (this.dataSource === source) return;
      this.dataSource = source;
      localStorage.setItem('dataSource', source);
      const now = Date.now();
      this.funds.forEach(fund => {
        fund.nextUpdateTime = now;
      });
      this.refreshCountdown = 10;
    },

    async handleSyncConfirm(newFunds) {
      // 串行处理每个基金，避免 JSONP 回调冲突
      for (const fundInput of newFunds) {
        try {
          // 调用 API 获取详情
          const fundData = await this.fetchFundData(fundInput.code);
          
          // 计算份额与收益
          const netValue = parseFloat(fundData.dwjz);
          const estNet = parseFloat(fundData.gsz);
          const dailyRate = parseFloat(fundData.gszzl);
          
          // holding 是用户输入的持有金额
          const holding = fundInput.holding;
          
          // 计算初始份额 = 持有金额 / 当前净值
          const share = holding / netValue;

          // 当日收益 = 份额 × (估算净值 − 昨收净值)
          const dailyProfit = share * (estNet - netValue);

          const newFund = {
            code: fundInput.code,
            name: fundData.name,
            holding: holding,
            share: share, // 保存份额
            dailyRate: dailyRate,
            netValue: netValue,
            dailyProfit: parseFloat(dailyProfit.toFixed(2)),
            gztime: fundData.gztime,
            totalProfit: 0, 
            totalProfitRate: 0 
          };
          const existing = await db.getFundByCode(fundInput.code);
          if (existing) {
            await db.updateFund({ ...existing, ...newFund });
          } else {
            await db.addFund(newFund);
          }
        } catch (error) {
          console.error(`Error adding fund ${fundInput.code}:`, error);
          // 如果获取失败，仍然添加，但使用默认值
          const fallbackFund = {
            code: fundInput.code,
            name: `未知基金 (${fundInput.code})`,
            holding: fundInput.holding,
            dailyRate: 0,
            netValue: 1.0,
            dailyProfit: 0,
            totalProfit: 0,
            totalProfitRate: 0
          };
          const existing = await db.getFundByCode(fundInput.code);
          if (existing) {
            await db.updateFund({ ...existing, ...fallbackFund });
          } else {
            await db.addFund(fallbackFund);
          }
        }
      }
      // Reload list
      this.funds = await db.getAllFunds();
    },

    checkMarketStatus() {
      const now = new Date();
      const day = now.getDay(); // 0 is Sunday, 6 is Saturday
      const hour = now.getHours();
      const minute = now.getMinutes();
      
      // 周末休市
      if (day === 0 || day === 6) {
        this.isMarketClosed = true;
        return;
      }
      
      // 交易时间：09:30 - 15:00
      // 考虑到集合竞价和稍微的延迟，放宽到 09:15 - 15:05
      // 或者严格遵守用户要求“不要调用实时接口”，我们取一个较宽的交易时段，
      // 只要在这个时段外，就认为是休市。
      // 基金估值通常在 9:30 之后才有变化。
      
      const timeValue = hour * 100 + minute;
      // 930 to 1500
      if (timeValue >= 930 && timeValue <= 1500) {
        this.isMarketClosed = false;
      } else {
        this.isMarketClosed = true;
      }
    }
  }
}
</script>

<style>
/* 全局变量定义 */
:root {
  --primary-color: #1890ff;
  --success-color: #52c41a;
  --error-color: #ff4d4f;
  --bg-color: #f0f2f5;
  --card-bg: #ffffff;
  --text-primary: #1f1f1f;
  --text-secondary: #8c8c8c;
  --text-tertiary: #bfbfbf;
  --border-color: #f0f0f0;
  --border-radius: 12px;
  --box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  --header-height: 60px;
  --footer-height: 70px;
}

/* 全局重置 */
body {
  margin: 0;
  padding: 0;
  background-color: var(--bg-color);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: var(--text-primary);
}

#app {
  width: 100%;
  min-height: 100vh;
  display: flex;
  justify-content: center;
}

.app-container {
    width: 100%;
    max-width: 480px; /* 回归手机宽度，保持紧凑感 */
    margin: 0 auto;
    padding: 16px;
    padding-bottom: calc(var(--footer-height) + 20px);
    box-sizing: border-box;
    background-color: var(--bg-color);
    min-height: 100vh;
    position: relative;
  }

  /* 移动端适配：取消特殊 padding，保持一致 */
  @media (max-width: 480px) {
    .app-container {
      padding: 12px;
      padding-bottom: calc(var(--footer-height) + 20px);
    }
  }

/* 通用颜色 */
.red { color: var(--error-color) !important; }
.green { color: var(--success-color) !important; }
.text-right { text-align: right; }

/* 账户汇总卡片 */
.account-summary {
  margin-bottom: 20px;
}

.assets-card {
  background: linear-gradient(135deg, #aba7ae 0%, #f5a9ae 100%); /* 渐变紫红，充满活力 */
  border-radius: var(--border-radius);
  padding: 24px;
  color: white;
  box-shadow: 0 8px 20px rgba(123, 67, 151, 0.35);
  transition: transform 0.3s ease;
}

.assets-card:hover {
  transform: translateY(-2px);
}

.assets-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  font-size: 14px;
  opacity: 0.9;
}

.clickable {
  cursor: pointer;
  user-select: none;
  transition: opacity 0.2s;
}

.clickable:hover {
  opacity: 0.8;
}

.amount-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
}

.total-amount {
  font-size: 32px;
  font-weight: 700;
  line-height: 1.2;
}

.total-profit {
  font-size: 20px;
  font-weight: 600;
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.2);
  padding: 4px 12px;
  border-radius: 20px;
  backdrop-filter: blur(4px);
}

/* 列表控制栏 */
.list-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px; /* Align with list items */
  margin-bottom: 4px;
}

.market-status {
  font-size: 12px;
  color: var(--primary-color);
  background-color: rgba(24, 144, 255, 0.1);
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 600;
}

.header-stats {
  display: flex;
  gap: 16px;
}

.header-col {
  text-align: right;
  min-width: 70px;
  font-size: 12px;
  color: var(--text-secondary);
  font-weight: 500;
}

/* 基金列表 */
  .fund-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

.fund-item {
  background: var(--card-bg);
  border-radius: var(--border-radius);
  padding: 16px;
  box-shadow: var(--box-shadow);
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid transparent;
}

.fund-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
  border-color: var(--primary-color);
}

.fund-item:active {
  transform: scale(0.98);
}

.fund-main {
  flex: 1;
  min-width: 0; /* 防止溢出 */
  margin-right: 12px;
}

.fund-name {
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 6px;
  white-space: normal;
  overflow: visible;
  line-height: 1.4;
}

.fund-holdings {
  font-size: 13px;
  color: var(--text-secondary);
  font-family: monospace; /* 数字对齐 */
}

.fund-stats {
  display: flex;
  align-items: center;
  gap: 16px;
}

.stat-col {
  text-align: right;
  min-width: 70px;
}

.stat-value {
  font-size: 16px;
  font-weight: 600;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}

/* 底部操作区 */
.list-footer {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  width: calc(100% - 32px);
  max-width: 448px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);
  border-radius: 24px;
  padding: 8px 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 100;
  box-sizing: border-box;
  border: 1px solid rgba(255, 255, 255, 0.5);
}

.left-actions {
  display: flex;
  gap: 8px;
}

.icon-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 16px;
  background: rgba(0, 0, 0, 0.03);
  color: var(--text-primary);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  user-select: none;
}

.icon-btn:active {
  transform: scale(0.96);
  background: rgba(0, 0, 0, 0.06);
}

.icon {
  font-size: 14px;
}

/* center-info removed */

.data-source-toggle {
  display: flex;
  background: rgba(0, 0, 0, 0.05);
  padding: 3px;
  border-radius: 18px;
  position: relative;
}

.toggle-item {
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
  border-radius: 15px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  user-select: none;
  position: relative;
  z-index: 1;
}

.toggle-item.active {
  color: #1890ff;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}
</style>